import { useEffect } from "react";
import { createWithEqualityFn as create } from "zustand/traditional";
import { useParams } from "react-router-dom";

import { PuzzleMetadata } from "sockapi/puzzle-data";
import { AdminQueueFilterType } from "sockapi/scope-specs/admin-queue-spec";
import Globals from "Globals";
import { useShouldDisplayAdmin } from "stores/ServerInteractionStore";
import {
  useAccessiblePuzzles,
  useAccessibleRounds,
} from "stores/ScopeStateStore";
import { useIsAdminEnabledStore } from "stores/UserPreferencesStore";
import {
  RouteName,
  DistributiveRouteSpec,
  resolveRoute,
  useRouteMatch,
} from "components/urls";
import {
  DESERT_ISLAND_ROUND_ID,
  DESERT_ISLAND_PIECE_SLUG_PREFIX,
  DESERT_ISLAND_NUM_PIECES,
} from "components/PuzzleContent/IslandFinder";

/** Command to trigger a router redirect. */
type RedirectCommand = {
  path: string;
  replace?: boolean;
  isManual?: true;
};

type RedirectRoute<R extends RouteName> = DistributiveRouteSpec<R> & {
  replace?: boolean;
};

interface NavStoreState {
  /**
   * Whether the page has a React Router component. This allows us to
   * hook up navbar links to React Router without making them part of
   * the main React component.
   */
  hasRouter: boolean;
  setHasRouter: (hasRouter: boolean) => void;

  /**
   * Setting this triggers an in-app redirect, and then resets this state.
   */
  redirectCommand: RedirectCommand | null;
  setRedirectCommand: (redirectCommand: RedirectCommand | null) => void;
  setRedirectRoute: <R extends RouteName>(
    redirectRoute: RedirectRoute<R>
  ) => void;

  /**
   * Setting this forces the wrapup custom puzzles display to a specific
   * puzzle and scrolls to it. Only works on the wrapup page.
   */
  wrapupCustomPuzzleRedirectCommand: string | null;
  setWrapupCustomPuzzleRedirectCommand: (
    wrapupCustomPuzzleRedirectCommand: string | null
  ) => void;

  bigBoardTeamFilter: string;
  setBigBoardTeamFilter: (bigBoardTeamFilter: string) => void;
  bigBoardPuzzleFilter: string;
  setBigBoardPuzzleFilter: (bigBoardPuzzleFilter: string) => void;

  adminQueuePageNum: number;
  setAdminQueuePageNum: (adminQueuePageNum: number) => void;

  adminQueueTypeFilter: { [filterType in AdminQueueFilterType]: boolean };
  setAdminQueueTypeFilter: (adminQueueTypeFilter: {
    [filterType in AdminQueueFilterType]: boolean;
  }) => void;
  adminQueueTeamNameFilter: string;
  setAdminQueueTeamNameFilter: (adminQueueTeamNameFilter: string) => void;

  isAdminQueuePosthuntView: boolean;
  setIsAdminQueuePosthuntView: (isAdminQueuePosthuntView: boolean) => void;
  adminQueueShowPublicPuzzles: boolean;
  setAdminQueueShowPublicPuzzles: (
    adminQueueShowPublicPuzzles: boolean
  ) => void;
}

export const useNavStore = create<NavStoreState>((set) => ({
  hasRouter: false,
  setHasRouter: (hasRouter) => set({ hasRouter }),

  redirectCommand: null,
  setRedirectCommand: (redirectCommand) => set({ redirectCommand }),
  setRedirectRoute: <R extends RouteName>(redirectRoute: RedirectRoute<R>) => {
    const { replace } = redirectRoute;
    const redirectCommand = { path: resolveRoute<R>(redirectRoute), replace };
    set({ redirectCommand });
  },

  wrapupCustomPuzzleRedirectCommand: null,
  setWrapupCustomPuzzleRedirectCommand: (wrapupCustomPuzzleRedirectCommand) =>
    set({ wrapupCustomPuzzleRedirectCommand }),

  bigBoardTeamFilter: "",
  setBigBoardTeamFilter: (bigBoardTeamFilter) => set({ bigBoardTeamFilter }),
  bigBoardPuzzleFilter: "",
  setBigBoardPuzzleFilter: (bigBoardPuzzleFilter) =>
    set({ bigBoardPuzzleFilter }),

  adminQueuePageNum: 0,
  setAdminQueuePageNum: (adminQueuePageNum) => set({ adminQueuePageNum }),

  adminQueueTypeFilter: {
    [AdminQueueFilterType.HINT]: true,
  },
  setAdminQueueTypeFilter: (adminQueueTypeFilter) =>
    set({ adminQueueTypeFilter }),

  adminQueueTeamNameFilter: "",
  setAdminQueueTeamNameFilter: (adminQueueTeamNameFilter) =>
    set({ adminQueueTeamNameFilter }),

  isAdminQueuePosthuntView: false,
  setIsAdminQueuePosthuntView: (isAdminQueuePosthuntView) =>
    set({ isAdminQueuePosthuntView }),
  adminQueueShowPublicPuzzles: true,
  setAdminQueueShowPublicPuzzles: (adminQueueShowPublicPuzzles) =>
    set({ adminQueueShowPublicPuzzles }),
}));

export const useSlug = (): string | null => {
  const { slug } = useParams<{
    slug?: string;
  }>();

  if (Globals.slug !== undefined) return Globals.slug;
  if (slug !== undefined) return slug;

  return null;
};

/**
 * Parses the slug.
 * Returns null in the slug field if no slug is found.
 * Returns null in the puzName field if the slug does not match
 * any unlocked puzzle.
 */
export const useParsedSlug = (): {
  slug: string | null;
  puzName: string | null;
  puzData: PuzzleMetadata | null;
  desertIslandPieceId?: number;
} => {
  const slug = useSlug();
  const puzzles = useAccessiblePuzzles() ?? {};
  const getPuzDataBySlug = (slug: string): PuzzleMetadata | null => {
    return (
      Object.values(puzzles).find((puzData) => puzData.slug === slug) ?? null
    );
  };
  const {
    puzData,
    desertIslandPieceId,
  }: { puzData: PuzzleMetadata | null; desertIslandPieceId?: number } = (() => {
    if (slug === null) return { puzData: null };
    if (slug.startsWith(`${DESERT_ISLAND_PIECE_SLUG_PREFIX}_`)) {
      const pieceIdStr = slug.slice(DESERT_ISLAND_PIECE_SLUG_PREFIX.length + 1);
      if (/^\d+$/.test(pieceIdStr)) {
        if (
          Number(pieceIdStr) >= 1 &&
          Number(pieceIdStr) <= DESERT_ISLAND_NUM_PIECES
        )
          return { puzData: null, desertIslandPieceId: Number(pieceIdStr) };
      }
    }
    return { puzData: getPuzDataBySlug(slug) };
  })();
  return {
    slug,
    puzName: puzData?.puzName ?? null,
    puzData,
    desertIslandPieceId,
  };
};

export const usePuzName = (): string | null => {
  const parsedSlug = useParsedSlug();
  return parsedSlug?.puzName ?? null;
};

export const useInRound = () => useRouteMatch("round") !== null;

export const useRound = () => {
  const inRound = useInRound();
  const { slug, puzData, desertIslandPieceId } = useParsedSlug();
  const rounds = useAccessibleRounds();

  return (
    (rounds &&
      Object.values(rounds).find((round) =>
        desertIslandPieceId !== undefined
          ? round.roundId === DESERT_ISLAND_ROUND_ID
          : inRound
            ? round.slug === slug
            : round.roundId === puzData?.roundId
      )) ??
    null
  );
};

export const useInPuzzlePage = () => {
  const inPuzzleNav = useRouteMatch("puzzles") !== null;
  const inPuzzle = useRouteMatch("puzzle") !== null;
  const inPiece = useRouteMatch("piece") !== null;
  const inSolution = useRouteMatch("solution") !== null;
  const inHints = useRouteMatch("hints") !== null;
  const inStats = useRouteMatch("stats") !== null;
  return {
    inPuzzleNav,
    inPuzzle,
    inPiece,
    inSolution,
    inHints,
    inStats,
    inPuzzlePage:
      inPuzzleNav || inPiece || inPuzzle || inSolution || inHints || inStats,
  };
};

export const useRedirectIfNonAdmin = () => {
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const isAdminEnabled = useIsAdminEnabledStore(
    (state) => state.isAdminEnabled
  );
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const currPath = useRouteMatch("puzzles");

  useEffect(() => {
    if (isAdminEnabled) return;
    if (shouldDisplayAdmin) return;
    if (currPath) return;
    toastr.error("Permission denied.");
    setRedirectRoute({ route: "puzzles", replace: true });
  }, [isAdminEnabled, shouldDisplayAdmin, setRedirectRoute, currPath]);
};
